import { useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SpecialCard from "@/components/feature/SpecialCard";
import useSeo from "@/hooks/useSeo";
import { allSpecials, getMoviesByIds, getSpecialById } from "@/utils/data";

export default function Special() {
  const [searchParams] = useSearchParams();
  const sid = searchParams.get("sid") || "";
  const selected = useMemo(() => getSpecialById(sid), [sid]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected && panelRef.current) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [selected]);

  useSeo({
    title: selected
      ? `${selected.title} - 百合影视专题`
      : "专题合集 - 百合影视精选策展",
    description: selected
      ? selected.description
      : "百合影视专题合集页，按主题策展精选百合电影与电视剧，帮助你更快找到对味的那一类故事。",
    keywords: selected
      ? `${selected.title},百合影视专题,百合合集,${selected.subtitle}`
      : "百合影视专题,百合合集,百合推荐,专题策展",
  });

  const selectedMovies = selected ? getMoviesByIds(selected.movieIds) : [];

  return (
    <div className="pt-24 md:pt-32">
      <div className="border-b border-background-200/60 bg-secondary-50">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              {
                label: "专题合集",
                to: selected ? "/special" : undefined,
              },
              ...(selected ? [{ label: selected.title }] : []),
            ]}
          />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            专题合集
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            好的内容值得被重新组织。我们用主题策展的方式，把散落的好作品串成一条线，
            让挑选作品这件事，变得更有温度、也更省心。
          </p>
        </div>
      </div>

      {selected && (
        <section ref={panelRef} className="bg-primary-50 py-12 md:py-16">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6">
            <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center lg:gap-12">
              <div className="overflow-hidden rounded-lg bg-secondary-100">
                <div className="aspect-[16/10] w-full">
                  <img
                    src={selected.cover}
                    alt={`${selected.title} 专题主图`}
                    title={`${selected.title} 百合影视专题`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-50">
                  <i className="ri-bookmark-3-line" />
                  当前专题
                </span>
                <h2 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                  {selected.title}
                </h2>
                <p className="mt-2 text-sm text-primary-600 md:text-base">
                  {selected.subtitle}
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  {selected.description}
                </p>
                <p className="mt-4 text-xs text-foreground-500">
                  本专题共收录 {selectedMovies.length} 部作品
                </p>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
              {selectedMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              全部专题
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              点击任意专题卡片，即可展开查看该专题收录的作品
            </p>
          </div>
          <Link
            to="/category"
            className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700 sm:inline-flex"
          >
            浏览全部作品
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-line" />
            </span>
          </Link>
        </div>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {allSpecials.map((special) => (
            <SpecialCard key={special.id} special={special} />
          ))}
        </div>
      </section>
    </div>
  );
}